import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { StackActions, useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { Pressable, Image, View, ImageSourcePropType } from 'react-native';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import useStyles from '../../../hooks/styles-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import { navBarActions } from '../../../store/navBarSlice';

import Label, { LabelType } from '../Label';
import { drawerActions } from '../../../store/drawerSlice';
import LoggedInRestrictedArea from '../LoggedInRestrictedArea';
import { LoggedState } from '../../../models/AuthModelsCommon';
import useTranslation from '../../../hooks/translation-hook';
import { DeviceTheme } from '../../../api/api.g';

export interface NavBarProps {
  icon: IconProp;
  image?: ImageSourcePropType;
  title: string;
  screen: 'home' | 'map' | 'validation' | 'mymissions' | 'myreferences' | 'remotemissions';
  params?: any;
  showIndicator?: boolean;
}

const NavBarButton = (props: NavBarProps) => {
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const navigation = useNavigation();
  const navStore = useAppSelector((selector) => selector.navBar);
  const userTheme = useAppSelector((selector) => selector.userPrefs.theme);
  const styles = useStyles((section) => section.navBar);
  const theme = useCustomTheme();
  const authStore = useAppSelector((selector) => selector.auth);

  const [showIndicator, setShowIndicator] = useState(props.showIndicator);
  const [active, setActive] = useState(false);

  const [shouldShowAnonymousPopup, setShouldShowAnonymousPopup] = useState(false);

  useEffect(() => {
    setShowIndicator(props.showIndicator);
  }, [props.showIndicator]);

  const getActivePagePath = (referenceMenu: string) => {
    if (referenceMenu === 'mymissions') {
      return navStore.currentPath === 'mymissions' || navStore.currentPath === 'survey';
    } else {
      return referenceMenu === navStore.currentPath;
    }
  };

  const isActive = getActivePagePath(props.screen);

  return (
    <>
      {shouldShowAnonymousPopup && (
        <LoggedInRestrictedArea
          onlyPopup
          onPopupShow={() => setShouldShowAnonymousPopup(false)}
          message={t('need-login-before', { action: t(props.screen === 'myreferences' ? 'see-your-references' : 'see-your-missions') })}
        />
      )}
      <Pressable
        onPress={() => {
          const isRestrictedNavigation = props.screen != 'map';

          if (isRestrictedNavigation && authStore.logged === LoggedState.Anonymous) {
            setShouldShowAnonymousPopup(true);
            return;
          } else {
            setShouldShowAnonymousPopup(false);
          }

          dispatch(navBarActions.setPath(props.screen));
          dispatch(drawerActions.setContent());
          const state = (navigation as any)?.getState?.();
          const hasTargetRoute = !!state?.routes?.some((route) => route?.name === props.screen);

          if (hasTargetRoute && (StackActions as any)?.popTo) {
            (navigation as any).dispatch((StackActions as any).popTo(props.screen));
            return;
          }

          navigation.navigate(props.screen as never);
        }}
        style={styles.buttonBase}
      >
        {props.image != undefined ? (
          <Image
            style={[
              { height: 20, width: 60 },
              // In dark mode, tint monochrome PNG assets so narrow web and native match.
              userTheme === DeviceTheme.Dark ? { tintColor: theme.text.primary.toString() } : null,
            ]}
            resizeMode={'contain'}
            source={props.image}
          />
        ) : (
          <View>
            {showIndicator && <FontAwesomeIcon size={10} icon={faCircle} color={theme.indicatorColor.toString()} style={styles.button.indicator} />}
            <FontAwesomeIcon icon={props.icon} size={active ? 28 : 18} color={theme.text.primary.toString()} style={styles.button.image} />
          </View>
        )}
        <View style={{ alignItems: 'center', marginTop: 4 }}>
          <Label variant={LabelType.titleSmall}>{props.title}</Label>
          <View
            style={{ width: 4, height: 4, borderRadius: 2, marginTop: 4, backgroundColor: isActive ? theme.indicatorColor.toString() : 'transparent' }}
          ></View>
        </View>
      </Pressable>
    </>
  );
};

export default NavBarButton;
